import React, { FC, ReactElement, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Pagination,
  RadioChangeEvent,
  Drawer,
  Checkbox,
  Radio,
  Spin,
} from "antd";
import {
  FilterOutlined,
  CloseOutlined,
  AppstoreOutlined,
  SortAscendingOutlined,
} from "@ant-design/icons";
import { CheckboxValueType } from "antd/lib/checkbox/Group";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { BASE_URL } from "../../constants/urlConstants";

import PerfumeCard from "../../components/PerfumeCard/PerfumeCard";
import { MAX_PAGE_VALUE, usePagination } from "../../hooks/usePagination";
import { useSearch } from "../../hooks/useSearch";
import { FilterParamsType } from "../../types/types";
import { fetchPerfumesByFilterParams } from "../../redux-toolkit/perfumes/perfumes-thunks";
import {
  selectIsPerfumesLoading,
  selectPerfumes,
} from "../../redux-toolkit/perfumes/perfumes-selector";
import "./Menu.css";

export enum CheckboxCategoryFilter {
  BRANDS        = "BRANDS",
  CATEGORIES    = "CATEGORIES",
  SUBCATEGORIES = "SUBCATEGORIES",
  GENDERS       = "GENDERS",
}

type LocationStateType = { perfumes?: any[]; searchText?: string };

const PRICE_RANGES = [
  { id: 1, label: "Under ₹100",       array: [1, 99] },
  { id: 2, label: "₹100 – ₹499",      array: [100, 499] },
  { id: 3, label: "₹500 – ₹999",      array: [500, 999] },
  { id: 4, label: "₹1,000 – ₹4,999",  array: [1000, 4999] },
  { id: 5, label: "₹5,000 – ₹9,999",  array: [5000, 9999] },
];

/* ── Collapsible filter section ─────────────────────────── */
const FilterSection: FC<{
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}> = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mn-filter-section">
      <button className="mn-filter-section-hd" onClick={() => setOpen((p) => !p)}>
        <span>{title}</span>
        <span className={`mn-filter-chevron ${open ? "open" : ""}`}>›</span>
      </button>
      {open && <div className="mn-filter-section-body">{children}</div>}
    </div>
  );
};

/* ── Main component ──────────────────────────────────────── */
const Menu: FC = (): ReactElement => {
  const dispatch          = useDispatch();
  const perfumes          = useSelector(selectPerfumes) || [];
  const isPerfumesLoading = useSelector(selectIsPerfumesLoading);
  const location          = useLocation<LocationStateType>();

  const [brands, setBrands]               = useState<string[]>([]);
  const [categories, setCategories]       = useState<string[]>([]);
  const [subCategories, setSubCategories] = useState<string[]>([]);
  const [genders]                         = useState<string[]>(["Male", "Female", "Unisex"]);

  const [noProducts, setNoProducts]             = useState(false);
  const [skipReduxLoading, setSkipReduxLoading] = useState(false);

  const [filterParams, setFilterParams] = useState<FilterParamsType>({
    brands: [], categories: [], subCategories: [], genders: [], prices: [1, 9999],
  });

  // ── sortDirection ref — always current, never stale ──
  const sortDirRef            = useRef<string>("");
  const [sortDir, setSortDir] = useState<"asc" | "desc" | "">("");

  const { currentPage, totalElements, handleChangePagination, resetPagination } = usePagination();
  const { resetFields } = useSearch();

  const [localPerfumes, setLocalPerfumes]   = useState<any[]>([]);
  const [localTotal, setLocalTotal]         = useState<number>(0);
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const [drawerOpen, setDrawerOpen]         = useState(false);
  const [isMobile, setIsMobile]             = useState(window.innerWidth <= 768);

  const activeFiltersCount =
    (filterParams.brands?.length        || 0) +
    (filterParams.categories?.length    || 0) +
    (filterParams.subCategories?.length || 0) +
    (filterParams.genders?.length       || 0) +
    (filterParams.prices?.[1] !== 9999  ? 1 : 0);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* Fetch filter options */
  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/v1/perfumes/filters`)
      .then((res) => {
        setBrands(res.data.brands || []);
        setCategories(res.data.categories || []);
        setSubCategories(res.data.subCategories || []);
      })
      .catch(console.error);
  }, []);

  /* Main data load */
  useEffect(() => {
    const load = async () => {
      const qp         = new URLSearchParams(location.search);
      const brandParam = qp.get("brand");
      const searchText = qp.get("search") || "";

      setLocalPerfumes([]);
      setLocalTotal(0);
      setNoProducts(false);
      setSkipReduxLoading(false);

      if (brandParam) {
        setIsLocalLoading(true);
        try {
          const res = await axios.post(`${BASE_URL}/api/v1/perfumes/search?page=0`, {
            brands: [brandParam], categories: [], subCategories: [],
            genders: [], prices: [1, 9999], sortDirection: "", currentPage: 0,
          });
          const data = res.data || [];
          setLocalPerfumes(data);
          setLocalTotal(data.length);
        } finally {
          setIsLocalLoading(false);
        }
        return;
      }

      if (location.state?.perfumes) {
        setLocalPerfumes(location.state.perfumes);
        setLocalTotal(location.state.perfumes.length);
        return;
      }

      if (searchText) {
        setIsLocalLoading(true);
        setSkipReduxLoading(true);
        try {
          const res = await axios.post(
            `${BASE_URL}/api/v1/perfumes/search/text?page=0`,
            { searchType: "CATEGORY", text: searchText, currentPage: 0 }
          );
          const data = res.data || [];
          if (!data.length) { setNoProducts(true); return; }
          setLocalPerfumes(data);
          setLocalTotal(data.length);
        } finally {
          setIsLocalLoading(false);
        }
        return;
      }

      setIsLocalLoading(true);
      try {
        await dispatch(
          fetchPerfumesByFilterParams({
            ...filterParams,
            sortDirection: sortDirRef.current,
            currentPage: 0,
          })
        );
      } finally {
        setIsLocalLoading(false);
      }
    };

    load();
  }, [location.search, location.state]);

  /* ── Checkbox filter change ── */
  const onChangeCheckbox = (vals: CheckboxValueType[], cat: CheckboxCategoryFilter) => {
    const nf = { ...filterParams };
    if (cat === CheckboxCategoryFilter.BRANDS)        nf.brands        = vals as string[];
    if (cat === CheckboxCategoryFilter.CATEGORIES)    nf.categories    = vals as string[];
    if (cat === CheckboxCategoryFilter.SUBCATEGORIES) nf.subCategories = vals as string[];
    if (cat === CheckboxCategoryFilter.GENDERS)       nf.genders       = vals as string[];
    setFilterParams(nf);
    resetPagination();
    dispatch(
      fetchPerfumesByFilterParams({
        ...nf,
        sortDirection: sortDirRef.current, // ← always current, never stale
        currentPage: 0,
      })
    );
    resetFields();
  };

  /* ── Price range radio change ── */
  const onChangeRadio = (e: RadioChangeEvent) => {
    const prices = e.target.value?.array || [1, 9999];
    const nf = { ...filterParams, prices };
    setFilterParams(nf);
    resetPagination();
    dispatch(
      fetchPerfumesByFilterParams({
        ...nf,
        sortDirection: sortDirRef.current, // ← always current, never stale
        currentPage: 0,
      })
    );
  };

  /* ── Sort handler ── */
  const handleSort = (dir: "asc" | "desc") => {
    setSortDir(dir);
    sortDirRef.current = dir; // ← update ref synchronously before dispatch

    if (localPerfumes.length > 0) {
      // Sort local list client-side — Number() guards against string prices
      setLocalPerfumes((prev) =>
        [...prev].sort((a, b) => {
          const pa = Number(a.price);
          const pb = Number(b.price);
          return dir === "desc" ? pb - pa : pa - pb;
        })
      );
    } else {
      // Send sortDirection to backend
      resetPagination();
      dispatch(
        fetchPerfumesByFilterParams({
          ...filterParams,
          sortDirection: dir, // ← use local var, not stale state
          currentPage: 0,
        })
      );
    }
  };

  /* ── Clear all filters ── */
  const clearAllFilters = () => {
    const reset: FilterParamsType = {
      brands: [], categories: [], subCategories: [], genders: [], prices: [1, 9999],
    };
    setFilterParams(reset);
    setSortDir("");
    sortDirRef.current = ""; // ← reset ref alongside state
    resetPagination();
    dispatch(
      fetchPerfumesByFilterParams({ ...reset, sortDirection: "", currentPage: 0 })
    );
  };

  /* ── Pagination ── */
  const changePagination = (page: number, pageSize: number) => {
    handleChangePagination(page, pageSize);
    dispatch(
      fetchPerfumesByFilterParams({
        ...filterParams,
        sortDirection: sortDirRef.current, // ← always current, never stale
        currentPage: page - 1,
      })
    );
  };

  const displayList    = localPerfumes.length > 0 ? localPerfumes : perfumes;
  const effectiveTotal = localPerfumes.length > 0 ? localTotal : totalElements;
  const isLoading      = isLocalLoading || (!skipReduxLoading && isPerfumesLoading);

  /* ── Filter panel ── */
  const FilterPanel = (
    <div className="mn-filter-panel">
      <div className="mn-filter-header">
        <span className="mn-filter-title">
          <FilterOutlined /> Filters
        </span>
        {activeFiltersCount > 0 && (
          <button className="mn-filter-clear" onClick={clearAllFilters}>
            Clear all
          </button>
        )}
      </div>

      <FilterSection title="Brand">
        <Checkbox.Group
          className="mn-checkbox-group"
          value={filterParams.brands}
          onChange={(v) => onChangeCheckbox(v, CheckboxCategoryFilter.BRANDS)}
        >
          {brands.map((b) => (
            <Checkbox key={b} value={b} className="mn-checkbox">{b}</Checkbox>
          ))}
        </Checkbox.Group>
      </FilterSection>

      <FilterSection title="Category">
        <Checkbox.Group
          className="mn-checkbox-group"
          value={filterParams.categories}
          onChange={(v) => onChangeCheckbox(v, CheckboxCategoryFilter.CATEGORIES)}
        >
          {categories.map((c) => (
            <Checkbox key={c} value={c} className="mn-checkbox">{c}</Checkbox>
          ))}
        </Checkbox.Group>
      </FilterSection>

      <FilterSection title="Sub-Category" defaultOpen={false}>
        <Checkbox.Group
          className="mn-checkbox-group"
          value={filterParams.subCategories}
          onChange={(v) => onChangeCheckbox(v, CheckboxCategoryFilter.SUBCATEGORIES)}
        >
          {subCategories.map((s) => (
            <Checkbox key={s} value={s} className="mn-checkbox">{s}</Checkbox>
          ))}
        </Checkbox.Group>
      </FilterSection>

      <FilterSection title="Gender" defaultOpen={false}>
        <Checkbox.Group
          className="mn-checkbox-group"
          value={filterParams.genders}
          onChange={(v) => onChangeCheckbox(v, CheckboxCategoryFilter.GENDERS)}
        >
          {genders.map((g) => (
            <Checkbox key={g} value={g} className="mn-checkbox">{g}</Checkbox>
          ))}
        </Checkbox.Group>
      </FilterSection>

      <FilterSection title="Price Range">
        <Radio.Group className="mn-radio-group" onChange={onChangeRadio}>
          {PRICE_RANGES.map((r) => (
            <Radio key={r.id} value={r} className="mn-radio">{r.label}</Radio>
          ))}
        </Radio.Group>
      </FilterSection>
    </div>
  );

  return (
    <div className="mn-page">
      <div className="mn-layout">

        {/* ── Desktop sidebar ── */}
        {!isMobile && (
          <aside className="mn-sidebar">
            {FilterPanel}
          </aside>
        )}

        {/* ── Product area ── */}
        <main className="mn-main">

          {/* Toolbar */}
          <div className="mn-toolbar">
            <div className="mn-toolbar-left">
              {isMobile && (
                <button
                  className={`mn-filter-btn ${activeFiltersCount > 0 ? "has-filters" : ""}`}
                  onClick={() => setDrawerOpen(true)}
                >
                  <FilterOutlined />
                  Filters
                  {activeFiltersCount > 0 && (
                    <span className="mn-filter-badge">{activeFiltersCount}</span>
                  )}
                </button>
              )}
              <span className="mn-result-count">
                {isLoading
                  ? ""
                  : `${effectiveTotal > 0 ? effectiveTotal : displayList.length} Products`}
              </span>
            </div>

            <div className="mn-sort-btns">
              <span className="mn-sort-label"><SortAscendingOutlined /> Sort:</span>
              <button
                className={`mn-sort-btn ${sortDir === "asc" ? "active" : ""}`}
                onClick={() => handleSort("asc")}
              >
                Price: Low to High
              </button>
              <button
                className={`mn-sort-btn ${sortDir === "desc" ? "active" : ""}`}
                onClick={() => handleSort("desc")}
              >
                Price: High to Low
              </button>
            </div>
          </div>

          {/* Active filter chips */}
          {activeFiltersCount > 0 && (
            <div className="mn-active-chips">
              {filterParams.brands?.map((b) => (
                <span key={b} className="mn-chip">
                  {b}
                  <button
                    onClick={() =>
                      onChangeCheckbox(
                        filterParams.brands!.filter((x) => x !== b),
                        CheckboxCategoryFilter.BRANDS
                      )
                    }
                  >
                    <CloseOutlined />
                  </button>
                </span>
              ))}
              {filterParams.categories?.map((c) => (
                <span key={c} className="mn-chip">
                  {c}
                  <button
                    onClick={() =>
                      onChangeCheckbox(
                        filterParams.categories!.filter((x) => x !== c),
                        CheckboxCategoryFilter.CATEGORIES
                      )
                    }
                  >
                    <CloseOutlined />
                  </button>
                </span>
              ))}
              {filterParams.genders?.map((g) => (
                <span key={g} className="mn-chip">
                  {g}
                  <button
                    onClick={() =>
                      onChangeCheckbox(
                        filterParams.genders!.filter((x) => x !== g),
                        CheckboxCategoryFilter.GENDERS
                      )
                    }
                  >
                    <CloseOutlined />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Top pagination */}
          {!isLoading && effectiveTotal > MAX_PAGE_VALUE && (
            <div className="mn-pagination">
              <Pagination
                current={currentPage}
                pageSize={MAX_PAGE_VALUE}
                total={effectiveTotal}
                showSizeChanger={false}
                onChange={changePagination}
                size="small"
              />
            </div>
          )}

          {/* Product grid */}
          {isLoading ? (
            <div className="mn-loading">
              <Spin size="large" />
            </div>
          ) : noProducts ? (
            <div className="mn-empty">
              <AppstoreOutlined className="mn-empty-icon" />
              <p className="mn-empty-text">No products found</p>
              <button className="mn-empty-clear" onClick={clearAllFilters}>
                Clear filters
              </button>
            </div>
          ) : (
            <div className="mn-grid">
              {displayList.map((perfume) => (
                <div key={perfume.id} className="mn-card-wrap">
                  <PerfumeCard perfume={perfume} />
                </div>
              ))}
            </div>
          )}

          {/* Bottom pagination */}
          {!isLoading && effectiveTotal > MAX_PAGE_VALUE && (
            <div className="mn-pagination">
              <Pagination
                current={currentPage}
                pageSize={MAX_PAGE_VALUE}
                total={effectiveTotal}
                showSizeChanger={false}
                onChange={changePagination}
                size="small"
              />
            </div>
          )}
        </main>
      </div>

      {/* Mobile filter drawer */}
      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        placement="left"
        width="80%"
        bodyStyle={{ padding: 0 }}
        headerStyle={{ display: "none" }}
      >
        <div className="mn-drawer-inner">
          <div className="mn-drawer-top">
            <span className="mn-filter-title"><FilterOutlined /> Filters</span>
            <button className="mn-drawer-close" onClick={() => setDrawerOpen(false)}>
              <CloseOutlined />
            </button>
          </div>
          {FilterPanel}
          <div className="mn-drawer-footer">
            <button className="mn-drawer-apply" onClick={() => setDrawerOpen(false)}>
              Show Results
            </button>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default Menu;